import { useEffect, useRef, useState } from 'react';
import {
  Button,
  Callout,
  Card,
  EmptyState,
  ErrorState,
  MetricCard,
  ProvenanceBadge,
  Skeleton
} from '@viniciomendesr/rayrovisk-design-system';
import { AccessStateScreen, accessStateOf } from '../access/AccessState';
import { useMediaKit, useRefreshMediaKit } from '../api/queries';
import type {
  MediaKitNetwork,
  MediaKitNetworkMetrics,
  Metric,
  MetricKind,
  Network,
  SocialAccount
} from '../api/types';
import { InstagramConnectCard } from './InstagramConnectCard';
import { SocialLinksCard } from './SocialLinksCard';
import {
  EMPTY_DESCRIPTION,
  EMPTY_TITLE,
  ERROR_DESCRIPTION,
  ERROR_TITLE,
  METRIC_LABEL,
  NETWORK_LABEL,
  PROVENANCE,
  PROVENANCE_META,
  refreshOutcome,
  REFRESH_LABEL,
  REFRESH_NOTE,
  REFRESH_QUEUED,
  REFRESH_STILL_RUNNING,
  UNSUPPORTED,
  WITHHELD
} from './mediaKitCopy';

const KINDS: MetricKind[] = [
  'audience',
  'posts',
  'lifetimeViews',
  'averageViews',
  'engagementRate'
];

/**
 * The card a network gets before it has ever been read. The row of labels is
 * what tells her which numbers will appear here; an empty card reads as broken,
 * and a row of zeros would be a claim about her account.
 */
function pending(network: Network): Metric[] {
  return KINDS.map((kind) => ({
    kind,
    value: null,
    state: UNSUPPORTED[network]?.[kind] ? 'unsupported' : 'withheld'
  }));
}

const integer = new Intl.NumberFormat('pt-BR');
const percent = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function formatted(metric: Metric): string {
  if (metric.value === null) {
    return '—';
  }
  return metric.kind === 'engagementRate'
    ? `${percent.format(metric.value)}%`
    : integer.format(metric.value);
}

function readAt(iso: string): string {
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(
    new Date(iso)
  );
}

/** The date range the sample actually covers — never "os últimos 90 dias". */
function windowNote(window: MediaKitNetworkMetrics['window']): string | undefined {
  if (window.size === 0 || !window.oldestAt || !window.newestAt) {
    return undefined;
  }
  const from = new Intl.DateTimeFormat('pt-BR', { day: 'numeric', month: 'long' });
  const posts = `${window.size} ${window.size === 1 ? 'publicação' : 'publicações'}`;
  const span = `de ${from.format(new Date(window.oldestAt))} a ${from.format(new Date(window.newestAt))}`;
  return window.partial
    ? `Suas ${posts} mais recentes, ${span}.`
    : `Suas ${posts}, ${span}.`;
}

/**
 * Why the number is missing, in the order that matters to her.
 *
 * The network's own silence outranks everything: if it could not be read today,
 * saying "essa conta não mostra esse número" would blame her settings for our
 * problem.
 */
function reasonFor(
  metric: Metric,
  account: SocialAccount,
  refreshNote: string | undefined
): string | undefined {
  if (metric.state === 'unsupported') {
    return UNSUPPORTED[account.network]?.[metric.kind];
  }
  if (metric.value !== null) {
    return undefined;
  }
  return refreshNote ?? WITHHELD;
}

/**
 * The screen before the first answer, in the shape of the answer.
 *
 * A spinner over the whole page said only "wait"; this says what is coming and
 * keeps the layout from jumping when it lands. `Skeleton` is `aria-hidden`, so
 * the `aria-busy` container is what a screen reader hears.
 */
function MediaKitSkeleton() {
  return (
    <section
      aria-busy
      aria-label="Carregando os seus números"
      style={{ display: 'flex', flexDirection: 'column', gap: 22, maxWidth: 760 }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <h1 style={{ font: '600 24px/1.2 var(--rvk-font-expressive)', margin: 0 }}>Meus números</h1>
        <Skeleton shape="text" width="60%" />
      </div>
      <Card layer="expressive" padding="lg">
        <MetricCard label="Alcance somado das suas redes" value="" emphasis="hero" loading />
      </Card>
      {[0, 1].map((card) => (
        <Card key={card} layer="functional" padding="lg">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Skeleton shape="text" width={140} />
            <div
              style={{
                display: 'grid',
                gap: 12,
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))'
              }}
            >
              {KINDS.map((kind) => (
                <MetricCard key={kind} label={METRIC_LABEL[kind]} value="" loading />
              ))}
            </div>
          </div>
        </Card>
      ))}
    </section>
  );
}

function NetworkCard({
  account,
  metrics,
  fetchedAt,
  dataSource,
  refreshing
}: {
  account: SocialAccount;
  metrics?: MediaKitNetworkMetrics;
  fetchedAt?: string;
  dataSource?: MediaKitNetwork['dataSource'];
  refreshing?: boolean;
}) {
  const refreshNote = account.refreshState === 'ok' ? undefined : REFRESH_NOTE[account.refreshState];
  const window = metrics ? windowNote(metrics.window) : undefined;

  return (
    <Card layer="functional" padding="lg">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div
          style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}
        >
          <h2 style={{ font: '600 17px/1.3 var(--rvk-font-expressive)', margin: 0 }}>
            {NETWORK_LABEL[account.network]}
          </h2>
          <span
            style={{
              font: '400 12px/1.4 var(--rvk-font-functional)',
              color: 'var(--rvk-text-tertiary)'
            }}
          >
            @{account.handle}
          </span>
        </div>

        {/* Hidden while this network is being read again: the note explains why
            its last read produced nothing, and leaving it up beside spinning
            cards claims a failure that has not happened yet. */}
        {refreshNote && !refreshing && (
          <Callout tone={account.refreshState === 'never' ? 'neutral' : 'warning'}>
            {refreshNote}
          </Callout>
        )}

        <div
          style={{
            display: 'grid',
            gap: 12,
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))'
          }}
        >
          {(metrics?.metrics ?? pending(account.network)).map((metric) => {
            const reason = reasonFor(metric, account, refreshNote);
            // Only a permanent absence is spelled out on the card itself. The
            // network being unreadable today is one sentence in the callout
            // above, not the same sentence repeated in every card under it.
            const spelledOut =
              metric.state === 'unsupported' ? UNSUPPORTED[account.network]?.[metric.kind] : undefined;
            return (
              <MetricCard
                key={metric.kind}
                label={METRIC_LABEL[metric.kind]}
                value={formatted(metric)}
                loading={refreshing}
                access={metric.value === null ? 'unavailable' : undefined}
                emptyLabel={spelledOut ?? '—'}
                accessReason={reason}
              />
            );
          })}
        </div>

        {metrics && metrics.top.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <h3
              style={{
                font: '600 10px/1 var(--rvk-font-functional)',
                letterSpacing: '.14em',
                textTransform: 'uppercase',
                color: 'var(--rvk-text-tertiary)',
                margin: 0
              }}
            >
              Publicações que mais engajaram
            </h3>
            <ol style={{ display: 'flex', flexDirection: 'column', gap: 6, margin: 0, paddingLeft: 18 }}>
              {metrics.top.map((post) => (
                <li
                  key={post.ref}
                  style={{
                    font: '400 13px/1.5 var(--rvk-font-functional)',
                    color: 'var(--rvk-text-secondary)'
                  }}
                >
                  {post.url ? (
                    <a href={post.url} target="_blank" rel="noopener noreferrer">
                      {post.caption || post.ref}
                    </a>
                  ) : (
                    (post.caption || post.ref)
                  )}
                </li>
              ))}
            </ol>
          </div>
        )}

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 10,
            font: '400 12px/1.4 var(--rvk-font-functional)',
            color: 'var(--rvk-text-tertiary)'
          }}
        >
          {/* `official` in the design system means observed in Meta Graph. A
              scraped page is not that, so the badge only appears for a snapshot
              she authorized Meta to send. */}
          {dataSource === 'meta' ? (
            <>
              <ProvenanceBadge kind="official">Oficial</ProvenanceBadge>
              <span>{PROVENANCE_META}</span>
            </>
          ) : (
            <span>{PROVENANCE[account.network]}</span>
          )}
          {window && (
            <>
              <span aria-hidden>·</span>
              <span>{window}</span>
            </>
          )}
          {fetchedAt && (
            <>
              <span aria-hidden>·</span>
              <span>lido em {readAt(fetchedAt)}</span>
            </>
          )}
        </div>
      </div>
    </Card>
  );
}

/** How long the screen keeps asking before it stops and says so. */
const POLL_CEILING_MS = 90_000;

type Baseline = Partial<Record<Network, string | null>>;

function baselineOf(accounts: SocialAccount[]): Baseline {
  return Object.fromEntries(accounts.map((account) => [account.network, account.lastRefreshAt]));
}

/**
 * Which networks have not answered yet.
 *
 * Compared against the value each account carried when she pressed the button,
 * never against the browser's clock: `last_refresh_at` is stamped by the
 * server, and a browser running a minute fast would see every network as
 * pending until the ceiling tripped.
 */
function stillReading(accounts: SocialAccount[], baseline: Baseline): Network[] {
  return accounts
    .filter((account) => account.lastRefreshAt === baseline[account.network])
    .map((account) => account.network);
}

export function MediaKitScreen() {
  const [baseline, setBaseline] = useState<Baseline | null>(null);
  const [gaveUp, setGaveUp] = useState(false);
  const [outcome, setOutcome] = useState('');
  const startedAt = useRef(0);

  const mediaKit = useMediaKit(baseline !== null);
  const refresh = useRefreshMediaKit();

  const accounts = mediaKit.data?.accounts ?? [];
  const pending = baseline ? stillReading(accounts, baseline) : [];

  // The baseline is taken after the queue is accepted, which is at least one
  // render after `startRefresh` was handed to a child. Through a ref rather
  // than the closure, so a network added by the card below is in the list this
  // waits on instead of being whatever the last render happened to hold.
  const latest = useRef(accounts);
  latest.current = accounts;

  useEffect(() => {
    if (!baseline) {
      return;
    }
    if (pending.length === 0) {
      const read = accounts.filter((a) => a.refreshState === 'ok').map((a) => a.network);
      setOutcome(
        refreshOutcome(
          read,
          accounts.filter((a) => a.refreshState !== 'ok').map((a) => a.network)
        )
      );
      setBaseline(null);
      return;
    }
    if (Date.now() - startedAt.current > POLL_CEILING_MS) {
      setGaveUp(true);
      setBaseline(null);
    }
    // `accounts` is rebuilt every render; `pending` is what actually changes.
  }, [baseline, pending.length, accounts]);

  function startRefresh() {
    setOutcome('');
    setGaveUp(false);
    startedAt.current = Date.now();
    refresh.mutate(undefined, {
      // Only on the 202: a refused queue leaves nothing to wait for, and
      // polling for it would spin until the ceiling and then blame the wait.
      onSuccess: () => setBaseline(baselineOf(latest.current))
    });
  }

  const state = accessStateOf(mediaKit.error);
  if (state) {
    return <AccessStateScreen state={state} />;
  }
  if (mediaKit.isPending) {
    return <MediaKitSkeleton />;
  }
  /**
   * A failed read is not an empty account, and until 2026-08-18 this screen said
   * it was: `data` is undefined either way, so a service that answered 404 —
   * which is what a deploy still serving the previous build answers — drew
   * `Adicione as suas redes` at someone who had just added them. She reads that
   * as her own work being lost, and nobody looking at the screen can tell the
   * two apart. `role="alert"` and a retry are the honest pair.
   */
  if (mediaKit.isError) {
    return (
      <ErrorState
        title={ERROR_TITLE}
        description={ERROR_DESCRIPTION}
        onRetry={() => void mediaKit.refetch()}
        retrying={mediaKit.isFetching}
      />
    );
  }

  const networks = mediaKit.data?.networks ?? [];
  const summary = mediaKit.data?.summary;

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 22, maxWidth: 760 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <h1 style={{ font: '600 24px/1.2 var(--rvk-font-expressive)', margin: 0 }}>Meus números</h1>
        <p
          style={{
            font: '400 14px/1.5 var(--rvk-font-functional)',
            color: 'var(--rvk-text-secondary)',
            margin: 0
          }}
        >
          O que já está público nas suas redes, reunido em um lugar só.
        </p>
      </div>

      {accounts.length === 0 ? (
        <EmptyState reason="no-data" title={EMPTY_TITLE} description={EMPTY_DESCRIPTION} framed />
      ) : (
        <>
          <Card layer="expressive" padding="lg">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <MetricCard
                label="Alcance somado das suas redes"
                value={summary ? formatted(summary.reach) : '—'}
                emphasis="hero"
                access={summary?.reach.value == null ? 'unavailable' : undefined}
                emptyLabel="—"
                accessReason="Ainda não lemos nenhuma das suas redes."
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <ProvenanceBadge kind="calculated">Calculado</ProvenanceBadge>
                {/* Seguidores somados são pessoas contadas mais de uma vez
                    quando ela publica nas três, e a soma tem que dizer isso. */}
                <span
                  style={{
                    font: '400 12px/1.4 var(--rvk-font-functional)',
                    color: 'var(--rvk-text-tertiary)'
                  }}
                >
                  Soma dos seguidores de cada rede. Quem segue você em duas conta duas vezes.
                </span>
              </div>
            </div>
          </Card>

          {/* One line at a time, and each one replaces the last: waiting, then
              what came back, or that we stopped waiting. Until 2026-08-19 the
              "estamos lendo" callout was drawn from `isSuccess` and therefore
              never left, whatever happened after it. */}
          {baseline !== null && (
            <Callout tone="info" live="status">
              {REFRESH_QUEUED}
            </Callout>
          )}
          {baseline === null && gaveUp && (
            <Callout tone="neutral" live="status">
              {REFRESH_STILL_RUNNING}
            </Callout>
          )}
          {baseline === null && !gaveUp && outcome !== '' && (
            <Callout tone="info" live="status">
              {outcome}
            </Callout>
          )}
          {refresh.isError && (
            <Callout tone="warning" live="status">
              {(refresh.error as Error).message}
            </Callout>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="secondary"
              loading={refresh.isPending || baseline !== null}
              onClick={startRefresh}
            >
              {REFRESH_LABEL}
            </Button>
          </div>

          {accounts.map((account) => (
            <NetworkCard
              key={account.network}
              account={account}
              metrics={summary?.networks.find((entry) => entry.network === account.network)}
              fetchedAt={networks.find((entry) => entry.network === account.network)?.fetchedAt}
              dataSource={networks.find((entry) => entry.network === account.network)?.dataSource}
              refreshing={pending.includes(account.network)}
            />
          ))}
        </>
      )}

      <SocialLinksCard accounts={accounts} onNetworkAdded={startRefresh} />
      <InstagramConnectCard />
    </section>
  );
}
